<template>
  <fullscreen-overlay-frame :title="$t('schedule-dialog.title').toString()"
                            icon="schedule"
                            color="primary"
                            centered
                            closable
                            maximizable
                            @close="close"
                            @maximize="maximize"
                            @minimize="minimize">
    <template v-slot:content>
      <div v-html="$t('schedule-dialog.description')"/>

      <content-card :title="$t('heating-mode-dialog.card-headline.general-information')"
                    icon="info"
                    class="my-5">
        <template v-slot:content>
          <v-list subheader>
            <div class="d-flex justify-space-between mx-5 my-2">
          <span class="mt-1 font-size-04" :class="schedule.isEnabled ? '' : 'font-weight-bold primary--text'">{{
              $t('schedule-dialog.inactive')
            }}</span>
              <v-switch class="mt-0"
                        v-model="schedule.isEnabled"
                        inset hide-details/>
              <span class="mt-1 font-size-04" :class="schedule.isEnabled ? 'primary--text font-weight-bold' : ''">{{
                  $t('schedule-dialog.active')
                }}</span>
            </div>
            <v-divider></v-divider>
            <!-- Name -->
            <v-list-item dense two-line class="ma-0">
              <v-list-item-icon class="my-auto">
                <v-icon class="material-icons-outlined" color="primary">label</v-icon>
              </v-list-item-icon>
              <v-list-item-content v-if="!editName">
                <v-list-item-title class="primary--text font-weight-bold">{{
                    additionalInformation.title
                  }}
                </v-list-item-title>
                <v-list-item-subtitle>{{ $t('schedule-dialog.schedule-title.label') }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-content v-else>
                <v-text-field outlined dense
                              hide-details
                              ref="device-name-input"
                              v-model="additionalInformation.title"/>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon @click="editName = true" v-if="!editName">
                  <v-icon class="material-icons-outlined" color="primary">edit</v-icon>
                </v-btn>
                <v-btn icon @click="editName = false" :disabled="additionalInformation.title.length < 1" v-else>
                  <v-icon class="material-icons-outlined" color="primary">check</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>

            <v-divider/>
            <!-- Color -->
            <v-list-item dense>
              <v-list-item-icon class="my-auto">
                <v-chip class="heating-scene-color-chip mt-1" :color="additionalInformation.color"></v-chip>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="primary--text font-weight-bold">
                  {{ $t('heating-mode-dialog.general-information-card.color-signature') }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon @click="editColor = true" v-if="!editColor">
                  <v-icon class="material-icons-outlined" color="primary">edit</v-icon>
                </v-btn>
                <v-btn icon @click="editColor = false" v-else>
                  <v-icon class="material-icons-outlined" color="primary">check</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>

            <v-list-item dense v-if="editColor">
              <swatch-color-picker class="mx-auto" :color="additionalInformation.color"
                                   @changeColor="changeScheduleColor"/>
            </v-list-item>

          </v-list>
        </template>
      </content-card>

      <charge-locations-list :selectable="true" v-model="schedule.locationId" :vehicle-id="data?.vehicleId"
                             origin-dialog='schedule'
                             :origin-data="{vehicleId: data?.vehicleId, schedule: {schedule: schedule, additionalInformation: additionalInformation}}"></charge-locations-list>

      <content-card :title="$t('schedule-dialog.rules.title')"
                    icon="update" class="mt-5">
        <template v-slot:titleBar>
          <v-icon class="add_location_icon material-icons-outlined"
                  color="primary" @click="newRule()">
            add_circle
          </v-icon>
        </template>
        <template v-slot:content>
          <v-list>
            <div v-for="(rule, i) in schedule.rules" :key="i">
              <v-list-item dense>
                <v-list-item-icon class="my-auto">
                  <v-icon color="primary">rule</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="primary--text font-weight-bold">
                    {{ $t('schedule-dialog.rules.rule') }} {{ i + 1 }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon @click="editRule(i, true)" v-if="editingRule !== i">
                    <v-icon class="material-icons-outlined" color="primary">edit</v-icon>
                  </v-btn>
                  <v-btn icon @click="editRule(i,false)" v-else>
                    <v-icon class="material-icons-outlined" color="primary">check</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>

              <v-list-item dense v-if="editingRule === i">
                <v-form class="rules-form">
                  <div class="my-5">
                    <div class="my-4">
                      <div class="d-flex justify-space-between">
                        <v-chip class="mx-1 white--text font-size-02 font-weight-bold"
                                v-for="(day, key) in weekdays"
                                v-bind:key="key"
                                :color="rule.weekdays.includes(day.value) ? 'primary' : 'blue-grey lighten-4'"
                                @click="toggleDay(i, day.value)">
                          {{ $t(day.translation) }}
                        </v-chip>
                      </div>
                      <div v-show="rule.weekdays.length < 1" class="mt-2 error-text">{{
                          $t('schedule-dialog.at-least-one-day-must-be-selected')
                        }}
                      </div>
                    </div>
                    <v-dialog
                        :ref="'dialogFrom' + i"
                        v-model="timePickerMenuFrom[i]"
                        :return-value.sync="rule.hourMinute.from"
                        width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            outlined
                            v-model="rule.hourMinute.from"
                            :label="$t('schedule-dialog.from.text-field-label')"
                            readonly
                            required
                            :rules="rulesFrom"
                            v-bind="attrs"
                            v-on="on"
                        ></v-text-field>
                      </template>
                      <v-time-picker
                          v-if="timePickerMenuFrom[i]"
                          v-model="rule.hourMinute.from"
                          full-width
                          format="24hr"
                          @click:minute="saveFrom(i, rule)"
                      >
                      </v-time-picker>
                    </v-dialog>
                    <v-dialog
                        :ref="'dialogTo' + i"
                        v-model="timePickerMenuTo[i]"
                        :return-value.sync="rule.hourMinute.to"
                        width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            outlined
                            v-model="rule.hourMinute.to"
                            :label="$t('schedule-dialog.to.text-field-label')"
                            readonly
                            required
                            :rules="rulesTo"
                            v-bind="attrs"
                            v-on="on"
                        ></v-text-field>
                      </template>
                      <v-time-picker
                          v-if="timePickerMenuTo[i]"
                          v-model="rule.hourMinute.to"
                          full-width
                          format="24hr"
                          @click:minute="saveTo(i, rule)"
                      >
                      </v-time-picker>
                    </v-dialog>
                    <!-- delete button -->
                    <v-btn :disabled="schedule.rules.length <= 1"
                           large outlined depressed
                           color="primary"
                           class="font-weight-bold action-button"
                           @click="deleteRule(i)">
                      <v-icon left>delete</v-icon>
                      {{ $t('schedule-dialog.delete-rule') }}
                    </v-btn>
                  </div>
                </v-form>
              </v-list-item>
            </div>
          </v-list>
        </template>
      </content-card>
    </template>
    <template v-slot:actions>

      <!-- save button -->
      <v-btn v-if="!editing"
             :disabled="submitting || !valid"
             :loading="submitting"
             large depressed block
             color="primary"
             class="font-weight-bold action-button"
             @click="submitSchedule">
        <v-icon left>add</v-icon>
        {{ $t('schedule-dialog.save') }}
      </v-btn>

      <!-- update button -->
      <v-btn v-if="editing"
             :disabled="submitting || !valid"
             :loading="submitting"
             large depressed
             block
             color="primary"
             class="font-weight-bold action-button"
             @click="submitSchedule">
        <v-icon left>update</v-icon>
        {{ $t('schedule-dialog.update') }}
      </v-btn>

      <!-- delete button -->
      <v-btn v-if="editing"
             :disabled="submitting"
             large outlined depressed block
             color="primary"
             class="mt-4 ml-0 font-weight-bold action-button"
             @click="deleteSchedule">
        <v-icon left>delete</v-icon>
        {{ $t('schedule-dialog.delete') }}
      </v-btn>
    </template>
  </fullscreen-overlay-frame>
</template>

<script>

import FullscreenOverlayFrame from "@/templates/dialogs/FullscreenOverlayFrame.vue";
import ContentCard from "@/templates/components/ContentCard.vue";
import {createEnodeLib} from "@/plugins/enodePlugin";
import ChargeLocationsList from "@/templates/components/ChargeLocationList.vue";
import SwatchColorPicker from "@/templates/components/SwatchColorPicker.vue";

export default {
  components: {
    SwatchColorPicker,
    ChargeLocationsList,
    ContentCard,
    FullscreenOverlayFrame
  },
  props: ['data'],
  data() {
    return {
      fullscreen: false,
      schedule: {
        isEnabled: true,
        defaultShouldCharge: false,
        rules: [
          {
            hourMinute: {
              from: null,
              to: null
            },
            weekdays: [0, 1, 2, 3, 4],
            shouldCharge: true,
          },
        ],
        targetId: "",
        targetType: "vehicle",
        locationId: null,
      },
      additionalInformation: {
        title: this.$t('schedule-dialog.additional-information.standard-title').toString(),
        color: '#FFB000'
      },
      timePickerMenuFrom: [false],
      timePickerMenuTo: [false],
      rulesFrom: [
        v => !!v || this.$t('app.rules.required')
      ],
      rulesTo: [
        v => !!v || this.$t('app.rules.required')
      ],
      scheduleTitleRule: [
        v => !!v || this.$t('app.rules.required')
      ],
      submitting: false,
      enodeLib: createEnodeLib(),
      editName: false,
      editColor: false,
      editingRule: null
    }
  },
  methods: {
    /**
     * maximize dialog
     */
    maximize() {
      this.fullscreen = true
      this.$emit('maximize')
    },

    /**
     * minimize dialog
     */
    minimize() {
      this.fullscreen = false
      this.$emit('minimize')
    },

    /**
     * close dialog
     */
    close() {
      this.$root.bisadialog.toggle('scheduleOverview', true, {
        vehicleId: this.data.vehicleId
      })
      this.$root.bisadialog.toggle('schedule')
    },
    init() {
      if (this.data?.schedule) {
        this.schedule = this.data?.schedule?.schedule
        this.additionalInformation = this.data?.schedule?.scheduleAdditionalInformation
      }
      this.schedule.targetId = this.data?.vehicleId
    },
    toggleDay(index, value) {
      if (this.schedule.rules[index].weekdays.includes(value)) {
        this.schedule.rules[index].weekdays.splice(this.schedule.rules[index].weekdays.indexOf(value), 1)
      } else {
        this.schedule.rules[index].weekdays.push(value)
      }
    },
    submitSchedule() {
      this.submitting = true
      if (this.data?.smartChargingEnabled || this.data?.greenChargingActive) {
        this.$root.bisadialog.toggle('confirmation', true, {
          text: this.$t('schedule-dialog.different-rule-active'),
          confirmLabel: this.$t('schedule-dialog.confirm-rule-deactivation'),
        })
        this.$root.$on('dialogConfirmed', () => {
          this.$root.$off('dialogConfirmed')
          this.$root.$off('dialogAborted')
          this.$root.$emit('deactivateOtherRules-' + this.data?.vehicleId)
         //deactivateOtherRules- this.data?.vehicleId
          if (this.editing) {
            this.updateSchedule()
          } else {
            this.createSchedule()
          }
        })
        this.$root.$on('dialogAborted', () => {
          this.$root.$off('dialogConfirmed')
          this.$root.$off('dialogAborted')
        })
      } else {
        if (this.editing) {
          this.updateSchedule()
        } else {
          this.createSchedule()
        }
      }
    },
    createSchedule() {
      this.submitting = true
      this.enodeLib.createSchedule({
        schedule: this.schedule,
        scheduleAdditionalInformation: this.additionalInformation
      }).then(() => {
        this.submitting = false
        this.enodeLib.fetchUserSchedules()
        this.close()
      }).catch((error) => {
        console.error('Error creating schedule', error)
        this.submitting = false
      })
    },
    updateSchedule() {
      this.submitting = true
      this.enodeLib.updateSchedule({
        schedule: this.schedule,
        scheduleAdditionalInformation: this.additionalInformation
      }).then(() => {
        this.submitting = false
        this.enodeLib.fetchUserSchedules()
        this.close()
      }).catch((error) => {
        console.error('Error updating schedule', error)
        this.submitting = false
      })
    },
    deleteSchedule() {
      this.submitting = true
      this.enodeLib.deleteSchedule(this.schedule.id).then(() => {
        this.submitting = false
        this.enodeLib.fetchUserSchedules()
        this.close()
      }).catch((error) => {
        console.error('Error deleting schedule', error)
        this.submitting = false
      })
    },
    changeScheduleColor(newColor) {
      this.additionalInformation.color = newColor
    },
    newRule() {
      this.schedule.rules.push({
        hourMinute: {
          from: null,
          to: null
        },
        weekdays: [0, 1, 2, 3, 4],
        shouldCharge: true,
      })
    },
    editRule(index, state) {
      this.editingRule = state ? index : null
    },
    saveFrom(index, rule) {
      let refName = 'dialogFrom' + index
      this.$refs[refName][0].save(rule.hourMinute.from)
      this.$set(this.timePickerMenuFrom, index, false)
    },
    saveTo(index, rule) {
      let refName = 'dialogTo' + index
      this.$refs[refName][0].save(rule.hourMinute.to)
      this.$set(this.timePickerMenuTo, index, false)
    },
    deleteRule(index) {
      this.schedule.rules.splice(index, 1)
    }
  },
  computed: {
    weekdays() {
      return [{
        translation: 'heating-mode-dialog.weekdays.monday-shortcut',
        value: 0
      }, {
        translation: 'heating-mode-dialog.weekdays.tuesday-shortcut',
        value: 1
      }, {
        translation: 'heating-mode-dialog.weekdays.wednesday-shortcut',
        value: 2
      }, {
        translation: 'heating-mode-dialog.weekdays.thursday-shortcut',
        value: 3
      }, {
        translation: 'heating-mode-dialog.weekdays.friday-shortcut',
        value: 4
      }, {
        translation: 'heating-mode-dialog.weekdays.saturday-shortcut',
        value: 5
      }, {
        translation: 'heating-mode-dialog.weekdays.sunday-shortcut',
        value: 6
      }]
    },
    editing() {
      return this.data?.schedule?.schedule?.id
    },
    valid() {
      let locationSelected = this.schedule.locationId
      if (!locationSelected) {
        return false
      }
      let colorValid = this.additionalInformation.color
      if (!colorValid) {
        return false
      }
      let titleValid = this.additionalInformation.title && this.additionalInformation.title.length > 0
      if (!titleValid) {
        return false
      }
      for (let rule of this.schedule.rules) {
        let weekdaysValid = rule.weekdays.length > 0
        if (!weekdaysValid) {
          return false
        }
        let fromSet = rule.hourMinute.from
        if (!fromSet) {
          return false
        }
        let toSet = rule.hourMinute.to
        if (!toSet) {
          return false
        }
      }
      return true
    }
  }
}
</script>

<style scoped lang="scss">
@import '~@/styles/enode-schedules.scss';
</style>
